/**
 * `<NotListedFlow>` — "My camera isn't listed" dialogs C1 and C2 (ENG-4832, Board C).
 *
 * Two entry points to one screen, not a sequence (ENG-4832 / ENG-4924):
 *
 *   C1 "Can't find your camera?" — from the list's trailing action: empty,
 *      optional free-text Brand and Model, a "Did you mean …?" shortcut back
 *      into the list, Back / Continue.
 *   C2 "<Brand model> isn't supported yet" — from a not-yet-supported pick or
 *      a camera discovery recognised: the same fields prefilled with the
 *      picked / detected names, still editable; no suggestion. Back / Continue.
 *   C3 is the brand/model step itself and is not rendered here.
 *
 * Continue finishes on either screen and is never disabled; Back (and
 * Escape / outside click, reported as `closed`) returns to the list.
 *
 * Controlled and store-agnostic: the add wizard (`NotListedFlowConnected`) and
 * the device-config surface both mount it and own what Continue / Did-you-mean
 * mean for their state. The flow owns only its own screen and fires
 * `device_not_listed_closed` once per dismissal (ENG-4832:
 * `{ opened_from, outcome, did_you_mean_shown }`).
 */
import { useAnalytics } from '#~/analytics/hooks/index.js';
import { createDeviceIdentificationTracker } from '#~/analytics/domains/device-identification.js';
import type {
  NotListedOpenedFrom,
  NotListedOutcome
} from '#~/analytics/domains/device-identification.js';
import * as _Footer from '#~/common/editor/footer/index.js';
import { Sel, STagNew } from '#~/stag/tag.js';
import * as Dialog from '@studio/design-system/dialog';
import { Group } from '@studio/design-system/form';
import { Input } from '@studio/design-system/input';
import type { Brand, Model } from '@studio/models/device';
import type { ReactElement } from 'react';
import { useState } from 'react';
import type { NotListedStep } from '../store/types.js';
import type { NotListedQuery, NotListedSuggestion } from './suggestions.js';

export interface NotListedFlowProps {
  /** Dialog visibility. Each open starts from `step` with `initial` (or empty) fields. */
  open: boolean;

  /** Screen to open on: C1 from the list, C2 for a not-yet-supported camera. */
  step: NotListedStep;

  /**
   * Which entry point opened the flow; reported as `opened_from` on every
   * `device_not_listed_closed` (ENG-4832). Required so no mount forgets it.
   */
  openedFrom: NotListedOpenedFrom;

  /**
   * Listed models the typed text may name, best first. The caller applies the
   * match threshold; C1 offers the first entry as "Did you mean …?".
   */
  suggestions: (query: NotListedQuery) => NotListedSuggestion[];

  /** Back: leave the flow (to the list) without identifying anything. */
  onBack: () => void;

  /** The flow finished with these (trimmed, possibly empty) values. */
  onContinue: (typed: NotListedQuery) => void;

  /** The user picked the suggested listed model instead. */
  onDidYouMean: (pick: { brandId: Brand['id']; modelId: Model['id'] }) => void;

  /** Prefill: the picker text (C1), the picked / detected names (C2), or what C3 reopens. */
  initial?: NotListedQuery | undefined;

  /** Optional mirror of the typed text, e.g. to fetch models for a typed brand. */
  onQueryChange?: ((query: NotListedQuery) => void) | undefined;
}

/** C2 title subject: "<Brand Model>", whichever of the two was typed. */
export const typedCameraLabel = ({ brand, model }: NotListedQuery): string =>
  [brand.trim(), model.trim()].filter((part) => part.length > 0).join(' ');

/**
 * Ticket copy (ENG-4832, verbatim). The two bodies share their second half;
 * only the opening sentence differs between "we'll note it" (C1) and "we've
 * noted it" (C2, the camera is already known).
 */
const UNTIL_THEN =
  'Until then, you can add it for video and try a standard control connection. Smart features like Click to Center won\u2019t work with it yet.';
const COPY = {
  c1: {
    title: 'Can\u2019t find your camera?',
    body: `Tell us which camera you have, and we\u2019ll prioritize adding it. ${UNTIL_THEN}`
  },
  c2: {
    title: (typed: NotListedQuery): string =>
      `${typedCameraLabel(typed) || 'This camera'} isn\u2019t supported yet`,
    body: `We\u2019ve noted it, and we\u2019ll prioritize adding it. ${UNTIL_THEN}`
  }
} as const;

/** Open-state body; mounted per open so every open starts from fresh fields. */
const Screen = ({
  step,
  openedFrom,
  suggestions,
  onBack,
  onContinue,
  onDidYouMean,
  initial,
  onQueryChange
}: Omit<NotListedFlowProps, 'open'>): ReactElement => {
  const { track } = useAnalytics();
  const tracker = createDeviceIdentificationTracker(track);

  const [query, setQueryState] = useState<NotListedQuery>({
    brand: initial?.brand ?? '',
    model: initial?.model ?? ''
  });

  const setQuery = (next: NotListedQuery): void => {
    setQueryState(next);
    onQueryChange?.(next);
  };

  const typed: NotListedQuery = { brand: query.brand.trim(), model: query.model.trim() };
  // "Did you mean" is C1's shortcut back into the list; C2 names a camera the
  // list already has (as not yet supported), so it offers no suggestion.
  const suggestion = step === 'c1' ? suggestions(query)[0] : undefined;

  // Every way off the screen is one `device_not_listed_closed`.
  // `did_you_mean_shown` is whether the "Did you mean" button was on screen
  // when it closed — `suggestion` is what renders that button, and it is
  // never set on C2.
  const closed = (outcome: NotListedOutcome): void => {
    tracker.trackDeviceNotListedClosed({
      opened_from: openedFrom,
      outcome,
      did_you_mean_shown: suggestion !== undefined
    });
  };

  // Leave to the list the way Back does. `outcome` is what the analytics
  // report: the Back button is `back`, Escape / outside click is `closed`
  // (ENG-4832) — same navigation, different reported outcome.
  const leave = (outcome: Extract<NotListedOutcome, 'back' | 'closed'>): void => {
    closed(outcome);
    onBack();
  };

  const handleBack = (): void => {
    leave('back');
  };

  // Continue finishes on either screen, with whatever was typed — both fields
  // are optional (ENG-4832), so it is never gated.
  const handleContinue = (): void => {
    closed('continue');
    onContinue(typed);
  };

  const handleDidYouMean = (): void => {
    if (!suggestion) return;

    closed('did_you_mean');
    onDidYouMean({ brandId: suggestion.brandId, modelId: suggestion.modelId });
  };

  // Escape / outside click on a dialog with no close icon navigates like Back
  // but is reported as `closed`.
  const handleOpenChange = (nextOpen: boolean): void => {
    if (!nextOpen) leave('closed');
  };

  return (
    <Dialog.Dialog open onOpenChange={handleOpenChange}>
      <Dialog.Overlay>
        <Dialog.Content className='fixed left-1/2 top-1/2 flex w-[506px] -translate-x-1/2 -translate-y-1/2 flex-col rounded-12 bg-base-200 shadow-lg'>
          {/* Board C: no close icon; Back is the way out. */}
          <Dialog.Header
            closeable={false}
            title={step === 'c1' ? COPY.c1.title : COPY.c2.title(typed)}
          />

          <div className='flex flex-col gap-20 px-40 py-24'>
            <p className='text-sm text-muted'>{step === 'c1' ? COPY.c1.body : COPY.c2.body}</p>

            <Group label='Brand'>
              <Input
                focusOnMount
                onChange={(brand: string) => setQuery({ ...query, brand })}
                placeholder='e.g. Lumens'
                value={query.brand}
                {...STagNew(Sel.AddDeviceIdentification, 'not-listed-brand')}
              />
            </Group>

            <Group label='Model'>
              <Input
                onChange={(model: string) => setQuery({ ...query, model })}
                placeholder='e.g. VC-A61P'
                value={query.model}
                {...STagNew(Sel.AddDeviceIdentification, 'not-listed-model')}
              />
            </Group>

            {!!suggestion && (
              <button
                className='self-start text-sm text-primary underline'
                onClick={handleDidYouMean}
                type='button'
                {...STagNew(Sel.AddDeviceIdentification, 'did-you-mean')}
              >
                Did you mean {suggestion.label}?
              </button>
            )}
          </div>

          <Dialog.Footer>
            {/* Per rule: playwright-stag-selectors-only (ai.knowledge/platform) — the step is the uniqueID so e2e can tell C1 from C2. */}
            <_Footer.Back
              onClick={handleBack}
              {...STagNew(Sel.AddDeviceIdentification, 'not-listed-back', step)}
            />
            <_Footer.Next
              onClick={handleContinue}
              {...STagNew(Sel.AddDeviceIdentification, 'not-listed-continue', step)}
            />
          </Dialog.Footer>
        </Dialog.Content>
      </Dialog.Overlay>
    </Dialog.Dialog>
  );
};

/** `<NotListedFlow>` component. */
export const NotListedFlow = ({ open, ...props }: NotListedFlowProps): ReactElement | null =>
  open ? <Screen {...props} /> : null;
